import { useState, useEffect, useRef } from 'react';
import { Container } from '../../../types/canvas.types';
import { useComponentRegistry } from '../../../contexts/ComponentRegistryContext';
import { generateComponentId } from '../../../utils/IdGenerator';
import { findContainerRecursively } from '../../../utils/containerHelpers';

export const useContainerLayout = (initialContainers: Container[], initialRowMapping?: Record<string, number> ) => {
  const [containers, setContainers] = useState<Container[]>(initialContainers);
  const [containerRows, setContainerRows] = useState<Record<string, number>>(
    initialRowMapping || {}
  );
  const [rows, setRows] = useState<Container[][]>([]);
  const [selectedContainerId, setSelectedContainerId] = useState<string | null>(null);
  const { setContainerParent } = useComponentRegistry();
  const isCreatingContainer = useRef(false);

  useEffect(() => {
    if (JSON.stringify(initialContainers) !== JSON.stringify(containers)) {
      setContainers(initialContainers);
    }
  }, [initialContainers]);

  useEffect(() => {
    if (initialRowMapping && Object.keys(initialRowMapping).length > 0 && Object.keys(containerRows).length === 0) {
      setContainerRows(initialRowMapping);
      return;
    }
    
    if (containers.length > 0) {
      const updatedRows: Record<string, number> = { ...containerRows };
      let hasChanges = false;
      
      containers.forEach(container => {
        if (!container.metadata?.isNested) {
          if (updatedRows[container.id] === undefined) {
            if (container.metadata?.rowIndex !== undefined) {
              updatedRows[container.id] = container.metadata.rowIndex;
              hasChanges = true;
            } else {
              const sortedContainers = [...containers]
                .filter(c => !c.metadata?.isNested)
                .sort((a, b) => a.order - b.order);
              
              let currentRow = 0;
              let currentRowWidth = 0;
              let containersInRow = 0;
              
              for (const sortedContainer of sortedContainers) {
                const width = parseFloat(sortedContainer.configuration.width) || 100;
                
                if ((currentRowWidth + width > 100.01) || 
                    containersInRow >= 3 || 
                    (width >= 100 && containersInRow > 0)) {
                  currentRow++;
                  currentRowWidth = 0;
                  containersInRow = 0;
                }
                
                if (sortedContainer.id === container.id) {
                  updatedRows[container.id] = currentRow;
                  hasChanges = true;
                  break;
                }
                
                currentRowWidth += width;
                containersInRow++;
              }
            }
          }
        }
      });
      
      Object.keys(updatedRows).forEach(id => {
        if (!containers.find(c => c.id === id)) {
          delete updatedRows[id];
          hasChanges = true;
        }
      });
      
      if (hasChanges) {
        setContainerRows(updatedRows);
      }
    }
  }, [containers, initialRowMapping]);

  
  useEffect(() => {
    const updatedContainers = containers.map(container => {
      if (!container.metadata?.isNested) {
        const currentRowIndex = container.metadata?.rowIndex;
        const newRowIndex = containerRows[container.id];
        
        const finalRowIndex = newRowIndex !== undefined 
          ? newRowIndex 
          : currentRowIndex !== undefined 
            ? currentRowIndex 
            : 0;
        
        if (currentRowIndex !== finalRowIndex) {
          return {
            ...container,
            metadata: {
              ...container.metadata,
              type: container.metadata?.type || 'container' as 'container' | 'nested-container',
              isNested: container.metadata?.isNested || false,
              parentContainerId: container.metadata?.parentContainerId,
              rowIndex: finalRowIndex
            }
          };
        }
      }
      return container;
    });
    
    const missingContainers: Record<string, number> = {};
    containers.forEach(container => {
      if (!container.metadata?.isNested && containerRows[container.id] === undefined) {
        const rowIndex = container.metadata?.rowIndex ?? 0;
        missingContainers[container.id] = rowIndex;
      }
    });
    
    if (Object.keys(missingContainers).length > 0) {
      setContainerRows(prev => ({ ...prev, ...missingContainers }));
    }
    
    if (JSON.stringify(updatedContainers) !== JSON.stringify(containers)) {
      setContainers(updatedContainers);
    }
  }, [containerRows, containers]);

  useEffect(() => {
    const validateNestedContainers = (parentContainer: Container): Container => {
      if (!parentContainer.content.nestedContainers || parentContainer.content.nestedContainers.length === 0) {
        return parentContainer;
      }
      
      const nestedRowMap: Record<number, Container[]> = {};
      
      parentContainer.content.nestedContainers.forEach(nestedContainer => {
        const rowIndex = nestedContainer.metadata?.rowIndex ?? 0;
        if (!nestedRowMap[rowIndex]) {
          nestedRowMap[rowIndex] = [];
        }
        nestedRowMap[rowIndex].push(nestedContainer);
      });
      
      Object.keys(nestedRowMap).forEach(rowIdx => {
        const rowNumber = Number(rowIdx);
        const containersInRow = nestedRowMap[rowNumber];
        
        containersInRow.sort((a, b) => a.order - b.order);
        
        const totalWidth = containersInRow.reduce((sum, container) => {
          const width = parseFloat(container.configuration.width) || 100;
          return sum + width;
        }, 0);
        
        if (totalWidth > 100.01 || (containersInRow.some(c => parseFloat(c.configuration.width) >= 100) && containersInRow.length > 1)) {
          
          let currentRow = rowNumber;
          let currentRowWidth = 0;
          
          containersInRow.forEach(container => {
            const width = parseFloat(container.configuration.width) || 100;
            
            if ((currentRowWidth + width > 100.01) || (width >= 100 && currentRowWidth > 0)) {
              if (currentRowWidth > 0) {
                currentRow++;
                currentRowWidth = 0;
              }
            }
            
            if (container.metadata && container.metadata.rowIndex !== currentRow) {
              container.metadata.rowIndex = currentRow;
            }
            
            currentRowWidth += width;
            
            if (width >= 100) {
              currentRow++;
              currentRowWidth = 0;
            }
          });
        }
      });
      
      parentContainer.content.nestedContainers = parentContainer.content.nestedContainers.map(nc => validateNestedContainers(nc));
      
      return parentContainer;
    };
    
    const validatedContainers = containers.map(container => {
      if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
        return validateNestedContainers(container);
      }
      return container;
    });
    
    const rowMap: Record<number, Container[]> = {};
    
    validatedContainers.forEach(container => {
      if (!container.metadata?.isNested) {
        const rowIndex = containerRows[container.id] ?? container.metadata?.rowIndex ?? 0;
        
        if (!rowMap[rowIndex]) {
          rowMap[rowIndex] = [];
        }
        rowMap[rowIndex].push(container);
      }
    });
    
    let needsRecalculation = false;
    const recalculatedRows: Record<string, number> = { ...containerRows };
    
    Object.keys(rowMap).forEach(rowIdx => {
      const rowNumber = Number(rowIdx);
      const containersInRow = rowMap[rowNumber];
      
      containersInRow.sort((a, b) => a.order - b.order);
      
      const totalWidth = containersInRow.reduce((sum, container) => {
        const width = parseFloat(container.configuration.width) || 100;
        return sum + width;
      }, 0);
      
      if (totalWidth > 100.01 || (containersInRow.some(c => parseFloat(c.configuration.width) >= 100) && containersInRow.length > 1)) {
        needsRecalculation = true;
        
        let currentRowWidth = 0;
        let currentRowContainers: Container[] = [];
        let nextRowIndex = rowNumber;
        
        containersInRow.forEach(container => {
          const width = parseFloat(container.configuration.width) || 100;
          
          if ((currentRowWidth + width > 100.01) || (width >= 100 && currentRowContainers.length > 0) || (currentRowWidth > 0 && width >= 100)) { 
            nextRowIndex++;
            currentRowWidth = width;
            currentRowContainers = [container];
            recalculatedRows[container.id] = nextRowIndex;
          } else {
            currentRowWidth += width;
            currentRowContainers.push(container);
            recalculatedRows[container.id] = nextRowIndex;
          }
        });
      }
    });
    
    if (needsRecalculation) {
      setContainerRows(recalculatedRows);
      return; 
    }
    
    if (JSON.stringify(validatedContainers) !== JSON.stringify(containers)) {
      setContainers(validatedContainers);
    }
    
    Object.keys(rowMap).forEach(rowIdx => {
      rowMap[Number(rowIdx)].sort((a, b) => a.order - b.order);
    });
    
    const rowKeys = Object.keys(rowMap)
      .map(Number)
      .sort((a, b) => a - b);
    
    const groupedRows = rowKeys.map(key => rowMap[key] || []);
    
    setRows(groupedRows);
  }, [containers, containerRows]);

  const handleContainerClick = (containerId: string) => {
      const container = containers.find(c => c.id === containerId);
      const isNested = container?.metadata?.isNested === true;
      
      if (isNested) {
        return;
      }
      setSelectedContainerId(containerId);
  };

  const normalizeWidth = (width: string | number): string => {
    if (typeof width === 'number') {
      return `${width}%`;
    }
    
    if (typeof width === 'string') {
      if (width.endsWith('%') || width.endsWith('px')) {
        return width;
      }
      
      return `${width}%`;
    }
    
    return '100%';
  };

  const handleAddVerticalContainer = (rowIndex: number) => {
    if (rows[rowIndex] && rows[rowIndex].length < 3) {
      const currentContainersInRow = rows[rowIndex].length;
      const newTotalContainers = currentContainersInRow + 1;
      const newWidthPercent = Math.floor(100 / newTotalContainers);
      
      const newContainer: Container = {
        id: generateComponentId('container'),
        name: `Container ${containers.length + 1}`,
        order: containers.length,
        configuration: {
          width: normalizeWidth(newWidthPercent),
          height: 'auto',
          alignment: 'left',
          layout: 'vertical',
          showTitle: true,
          collapsible: false
        },
        metadata: {
          type: 'container',
          isNested: false,
          parentContainerId: undefined,
          rowIndex: rowIndex
        },
        content: {
          type: 'widget',
          componentId: 'empty',
          'widget-type': 'contentBlock',
          nestedContainers: []
        },
        usageInfo: {
          usedComponents: []
        }
      };
      
      const updatedContainers = containers.map(container => {
        if (rows[rowIndex].some(c => c.id === container.id)) {
          return {
            ...container,
            configuration: {
              ...container.configuration,
              width: normalizeWidth(newWidthPercent)
            }
          };
        }
        return container;
      });
      
      const allUpdatedContainers = [...updatedContainers, newContainer];
      
      const updatedContainerRows = {
        ...containerRows,
        [newContainer.id]: rowIndex
      };
      
      setContainerRows(updatedContainerRows);
      setContainers(allUpdatedContainers);
      setSelectedContainerId(newContainer.id);
      
      return allUpdatedContainers;
    }
    
    return containers;
  };

  const getNextContainerNumber = () => {
    const existingNumbers = containers
      .filter(c => !c.metadata?.isNested)
      .map(c => {
        const match = c.name.match(/Container (\d+)$/);
        return match ? parseInt(match[1]) : 0;
      });
    
    const maxNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;
    return maxNumber + 1;
  };


  const handleAddHorizontalContainer = (afterRowIndex: number) => {
    if (isCreatingContainer.current) {
      return containers;
    }
    isCreatingContainer.current = true;

    try {
      const newContainer: Container = {
        id: generateComponentId('container'),
        name: `Container ${getNextContainerNumber()}`,
        order: containers.length,
        configuration: {
          width: '100%',
          height: 'auto',
          alignment: 'left',
          layout: 'vertical',
          showTitle: true,
          collapsible: false
        },
        metadata: {
          type: 'container' as const,
          isNested: false,
          rowIndex: 0,
          parentContainerId: undefined
        },
        content: {
          type: 'widget',
          componentId: 'empty',
          'widget-type': 'contentBlock',
          nestedContainers: []
        },
        usageInfo: {
          usedComponents: []
        }
      };
    
      let updatedContainers = [...containers];
      const updatedRows: Record<string, number> = {};
      
      if (afterRowIndex === -1) {
        updatedRows[newContainer.id] = 0;
        if (newContainer.metadata) {
          newContainer.metadata.rowIndex = 0;
        }

        containers.forEach(container => {
          if (!container.metadata?.isNested) {
            const currentRow = containerRows[container.id] ?? container.metadata?.rowIndex ?? 0;
            updatedRows[container.id] = currentRow + 1;
          }
        });
        
        updatedContainers = updatedContainers.map(c => ({
          ...c,
          order: c.order + 1
        }));
        
        newContainer.order = 0;
        updatedContainers.unshift(newContainer);
        
      } else {
        const newRowIndex = afterRowIndex + 1;
        
        const allContainerRows: Record<string, number> = {};
        
        containers.forEach(container => {
          if (!container.metadata?.isNested) {
            const currentRow = containerRows[container.id] ?? container.metadata?.rowIndex ?? 0;
            allContainerRows[container.id] = currentRow;
          }
        });
        
        Object.entries(allContainerRows).forEach(([id, rowIdx]) => {
          if (rowIdx >= newRowIndex) {
            updatedRows[id] = rowIdx + 1;
          } else {
            updatedRows[id] = rowIdx;
          }
        });
        
        updatedRows[newContainer.id] = newRowIndex;
        if (newContainer.metadata) {
          newContainer.metadata.rowIndex = newRowIndex;
        }
        
        let insertOrder = 0;
        let maxOrderBeforeNewRow = -1;
        
        containers.forEach(container => {
          if (!container.metadata?.isNested) {
            const containerRow = allContainerRows[container.id] ?? 0;
            if (containerRow <= afterRowIndex) {
              maxOrderBeforeNewRow = Math.max(maxOrderBeforeNewRow, container.order);
            }
          }
        });
        
        insertOrder = maxOrderBeforeNewRow + 1;
        newContainer.order = insertOrder;
        
        updatedContainers = updatedContainers.map(container => {
          if (container.order >= insertOrder) {
            return {
              ...container,
              order: container.order + 1
            };
          }
          return container;
        });
        
        updatedContainers.push(newContainer);
        updatedContainers.sort((a, b) => a.order - b.order);
      }
      
      updatedContainers = updatedContainers.map(container => {
        if (!container.metadata?.isNested && updatedRows[container.id] !== undefined) {
          return {
            ...container,
            metadata: {
              type: (container.metadata?.type || 'container') as 'container' | 'nested-container',
              isNested: container.metadata?.isNested || false,
              parentContainerId: container.metadata?.parentContainerId,
              rowIndex: updatedRows[container.id]
            }
          };
        }
        return container;
      });
      
      setContainerRows(updatedRows);
      setContainers(updatedContainers);
      setSelectedContainerId(newContainer.id);
      
      return updatedContainers;
    } catch (error) {
      console.error('Error adding horizontal container:', error);
      return containers;
    } finally {
      setTimeout(() => {
        isCreatingContainer.current = false;
      }, 100);
    }
  
  };

  const handleAddNestedBetween = (parentId: string, beforeIndex?: number) => {
    const parentContainer = findContainerRecursively(containers, parentId);
    if (!parentContainer || !parentContainer.content.nestedContainers) {
        console.error(`Parent container ${parentId} not found or has no nested containers`);
        return containers;
    }
    
    const nestedContainers = parentContainer.content.nestedContainers;
    
    let newRowIndex = 0;
    
    if (beforeIndex !== undefined && beforeIndex < nestedContainers.length) {
        const beforeContainer = nestedContainers[beforeIndex];
        const beforeRowIndex = beforeContainer.metadata?.rowIndex || 0;
        
        const updatedNestedContainers = nestedContainers.map(nc => {
            const currentRowIndex = nc.metadata?.rowIndex || 0;
            if (currentRowIndex >= beforeRowIndex) {
                return {
                    ...nc,
                    metadata: {
                        type: nc.metadata?.type || 'nested-container' as const,
                        isNested: nc.metadata?.isNested ?? true,
                        parentContainerId: nc.metadata?.parentContainerId || parentId,
                        rowIndex: currentRowIndex + 1
                    }
                };
            }
            return nc;
        });
        
        newRowIndex = beforeRowIndex;
        
        const parentNestingLevel = parentContainer.metadata?.nestingLevel || (parentContainer.metadata?.isNested ? 1 : 0);
        
        const newContainer: Container = {
            id: generateComponentId('container'),
            name: `Container ${parentContainer.name.replace('Container ', '')}.${nestedContainers.length + 1}`,
            order: beforeIndex,
            metadata: {
                type: 'nested-container' as const,
                isNested: true,
                parentContainerId: parentId,
                rowIndex: newRowIndex,
                nestingLevel: parentNestingLevel + 1
            },
            configuration: {
                width: '100%',
                height: 'auto',
                alignment: 'left',
                layout: 'vertical',
                showTitle: true,
                collapsible: false
            },
            content: {
                type: 'widget' as const,
                componentId: 'empty',
                'widget-type': 'contentBlock',
                nestedContainers: [],
                items: []
            },
            usageInfo: {
                usedComponents: []
            }
        };
        
        setContainerParent(newContainer.id, parentId);
        
        updatedNestedContainers.splice(beforeIndex, 0, newContainer);
        
        
        const finalNestedContainers = updatedNestedContainers.map((nc, index) => ({
            ...nc,
            order: index
        }));
        
        const updateContainersRecursively = (containerList: Container[]): Container[] => {
            return containerList.map(container => {
                if (container.id === parentId) {
                    return {
                        ...container,
                        content: {
                            ...container.content,
                            nestedContainers: finalNestedContainers
                        }
                    };
                }
                
                if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
                    return {
                        ...container,
                        content: {
                            ...container.content,
                            nestedContainers: updateContainersRecursively(container.content.nestedContainers)
                        }
                    };
                }
                
                return container;
            });
        };
        
        const updatedContainers = updateContainersRecursively(containers);
        
        setContainers(updatedContainers);
        setSelectedContainerId(newContainer.id);
        
        return updatedContainers;
    }
    
    return handleAddNestedHorizontalContainer(parentId);
};

  const handleAddNestedVerticalContainer = (parentId: string, targetRowIndex?: number) => {
    const parentContainer = findContainerRecursively(containers, parentId);
    
    if (!parentContainer) {
        console.error(`Parent container ${parentId} not found`);
        return containers;
    }
    
    if (!parentContainer.content.nestedContainers) {
        parentContainer.content.nestedContainers = [];
    }
    
    const nestedContainers = parentContainer.content.nestedContainers;
    
    if (targetRowIndex === undefined || targetRowIndex === -1) {
        return handleAddNestedHorizontalContainer(parentId);
    }
    
    const sameRowContainers = nestedContainers.filter(nc => 
        nc.metadata?.rowIndex === targetRowIndex
    );
    
    if (sameRowContainers.length >= 3) {
        console.warn("Maximum of 3 containers per row reached");
        return containers;
    }
    
    const newContainerCount = sameRowContainers.length + 1;
    const newWidth = Math.floor(100 / newContainerCount);
    
    const parentNestingLevel = parentContainer.metadata?.nestingLevel || (parentContainer.metadata?.isNested ? 1 : 0);
    
    const newContainer: Container = {
        id: generateComponentId('nested-container'),
        name: `Container ${parentContainer.name.replace('Container ', '')}.${nestedContainers.length + 1}`,
        order: nestedContainers.length,
        metadata: {
            type: 'nested-container',
            isNested: true,
            parentContainerId: parentId,
            rowIndex: targetRowIndex,
            nestingLevel: parentNestingLevel + 1
        },
        configuration: {
            width: `${newWidth}%`,
            height: 'auto',
            alignment: 'left',
            layout: 'vertical',
            showTitle: true,
            collapsible: false
        },
        content: {
            type: 'widget',
            componentId: 'empty',
            'widget-type': 'contentBlock',
            nestedContainers: [],
            items: []
        },
        usageInfo: {
            usedComponents: []
        }
    };
    
    setContainerParent(newContainer.id, parentId);
    
    nestedContainers.forEach(nc => {
        if (nc.metadata?.rowIndex === targetRowIndex) {
            nc.configuration.width = `${newWidth}%`;
        }
    });
    
    nestedContainers.push(newContainer);
    
    const updateContainersRecursively = (containerList: Container[]): Container[] => {
        return containerList.map(container => {
            if (container.id === parentId) {
                return {
                    ...container,
                    content: {
                        ...container.content,
                        nestedContainers: [...(container.content.nestedContainers || [])]
                    }
                };
            }
            
            if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
                return {
                    ...container,
                    content: {
                        ...container.content,
                        nestedContainers: updateContainersRecursively(container.content.nestedContainers)
                    }
                };
            }
            
            return container;
        });
    };
    
    const updatedContainers = updateContainersRecursively(containers);
    setContainers(updatedContainers);
    setSelectedContainerId(newContainer.id);
    
    return updatedContainers;
  };
  
  const handleAddNestedHorizontalContainer = (parentId: string) => {
    const parentContainer = findContainerRecursively(containers, parentId);
    
    if (!parentContainer) {
        console.error(`Parent container ${parentId} not found`);
        return containers;
    }
    
    if (!parentContainer.content.nestedContainers) {
        parentContainer.content.nestedContainers = [];
    }
    
    const nestedContainers = parentContainer.content.nestedContainers;
    
    let targetRowIndex = 0;
    
    if (nestedContainers.length > 0) {
        const maxRowIndex = Math.max(...nestedContainers.map(nc => nc.metadata?.rowIndex || 0));
        targetRowIndex = maxRowIndex + 1;
    }
    
    const parentNestingLevel = parentContainer.metadata?.nestingLevel || (parentContainer.metadata?.isNested ? 1 : 0);
    
    const newContainer: Container = {
        id: generateComponentId('nested-container'),
        name: `Container ${parentContainer.name.replace('Container ', '')}.${nestedContainers.length + 1}`,
        order: nestedContainers.length,
        metadata: {
            type: 'nested-container',
            isNested: true,
            parentContainerId: parentId,
            rowIndex: targetRowIndex,
            nestingLevel: parentNestingLevel + 1
        },
        configuration: {
            width: '100%',
            height: 'auto',
            alignment: 'left',
            layout: 'vertical',
            showTitle: true,
            collapsible: false
        },
        content: {
            type: 'widget',
            componentId: 'empty',
            'widget-type': 'contentBlock',
            nestedContainers: [],
            items: []
        },
        usageInfo: {
            usedComponents: []
        }
    };
    
    setContainerParent(newContainer.id, parentId);
    
    parentContainer.content.nestedContainers.push(newContainer);
    
    const updateContainersRecursively = (containerList: Container[]): Container[] => {
        return containerList.map(container => {
            if (container.id === parentId) {
                return {
                    ...container,
                    content: {
                        ...container.content,
                        nestedContainers: [...(container.content.nestedContainers || [])]
                    }
                };
            }
            
            if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
                return {
                    ...container,
                    content: {
                        ...container.content,
                        nestedContainers: updateContainersRecursively(container.content.nestedContainers)
                    }
                };
            }
            
            return container;
        });
    };
    
    const updatedContainers = updateContainersRecursively(containers);
    setContainers(updatedContainers);
    setSelectedContainerId(newContainer.id);
    
    return updatedContainers;
};

  return {
    containers,
    setContainers,
    containerRows,
    setContainerRows,
    rows,
    selectedContainerId,
    handleContainerClick,
    handleAddHorizontalContainer,
    handleAddVerticalContainer,
    handleAddNestedHorizontalContainer,
    handleAddNestedVerticalContainer,
    handleAddNestedBetween,
    setSelectedContainerId
  };
};